import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Payment-status union — mirrors `PaymentStatus` from `@bravemove/types/booking`
 * verbatim. Kept local to avoid pulling `@bravemove/types` into `@bravemove/ui-web`'s
 * dependency graph (other primitives like `StateRibbon` + `MapView` follow the
 * same convention). MUST stay in sync with the source enum.
 */
export type PaymentStatus = 'unpaid' | 'pending_confirmation' | 'paid' | 'refunded';

export type PaymentStatusChipSize = 'sm' | 'md';

export interface PaymentStatusChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly state: PaymentStatus;
  readonly size?: PaymentStatusChipSize;
  /** Override the default state label. */
  readonly label?: string;
}

interface PaymentStateStyle {
  /** Tailwind class string for the chip background + foreground + border. */
  readonly box: string;
  /** Tailwind class string for the inline status dot. */
  readonly dot: string;
  /** Default English glyph (SVG/Unicode fallback). */
  readonly glyph: string;
  /** Default English label per the F17 booking:payment_status namespace. */
  readonly defaultLabel: string;
  /** Accessible role description for screen readers. */
  readonly a11yLabel: string;
}

/**
 * State→style map. Mirrors the spec in [`docs/design/screens/booking-lib.jsx`](../../../../../docs/design/screens/booking-lib.jsx)
 * `PaymentStatusChip` (line 78). Tone choices:
 *
 *  - `unpaid`               → neutral (surface-2 / ink-3) with wallet glyph
 *  - `pending_confirmation` → warning-flavoured with wallet glyph
 *  - `paid`                 → success-flavoured with check glyph
 *  - `refunded`             → info-flavoured (NOT danger) with refresh-arrow glyph
 *
 * The existing app-local `apps/admin/src/components/PaymentStatusChip.tsx` +
 * `apps/portal/src/components/booking/PaymentStatusChip.tsx` are 1:1 visually
 * compatible re: tone for unpaid/pending/paid. The shared primitive ships the
 * spec-canonical `refunded → info` tone (the app-local copies used danger).
 * Wave 2 consolidation will swap callsites and the new tone is the intentional
 * upgrade.
 */
const STATE_STYLE: Record<PaymentStatus, PaymentStateStyle> = {
  unpaid: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    glyph: '○',
    defaultLabel: 'Unpaid',
    a11yLabel: 'Payment status: unpaid',
  },
  pending_confirmation: {
    box: 'bg-warning/15 text-ink border border-warning/30',
    dot: 'bg-warning',
    glyph: '○',
    defaultLabel: 'Pending',
    a11yLabel: 'Payment status: pending confirmation',
  },
  paid: {
    box: 'bg-success/15 text-ink border border-success/30',
    dot: 'bg-success',
    glyph: '✓',
    defaultLabel: 'Paid',
    a11yLabel: 'Payment status: paid',
  },
  refunded: {
    box: 'bg-info/15 text-ink border border-info/30',
    dot: 'bg-info',
    glyph: '↻',
    defaultLabel: 'Refunded',
    a11yLabel: 'Payment status: refunded',
  },
};

const SIZE: Record<PaymentStatusChipSize, { box: string; dot: string }> = {
  // sm: padding ~2px 7px, font 9px per spec
  sm: { box: 'px-[7px] py-[2px] gap-1.5 text-[9px]', dot: 'size-[5px]' },
  // md: padding ~4px 9px, font 10px per spec
  md: { box: 'px-[9px] py-[4px] gap-1.5 text-[10px]', dot: 'size-[6px]' },
};

/**
 * F17 — `PaymentStatusChip` (shared primitive).
 *
 * Renders the orthogonal `payment_status` value as a monospace badge with
 * dot + glyph + label. Sibling to `LifecycleChip` — same in-row badge shape,
 * same size pair, just a different state union.
 *
 * Spec source: [`docs/design/screens/booking-lib.jsx`](../../../../../docs/design/screens/booking-lib.jsx)
 * `PaymentStatusChip` (line 78).
 *
 * F20 will wire the venue-staff "Mark paid" UI on top of this read-only chip.
 *
 * @example
 *   <PaymentStatusChip state="paid" />
 *   <PaymentStatusChip state="pending_confirmation" size="sm" />
 *   <PaymentStatusChip state="refunded" label="Refunded — full amount" />
 */
export function PaymentStatusChip({
  state,
  size = 'md',
  label,
  className,
  ...rest
}: PaymentStatusChipProps) {
  const style = STATE_STYLE[state];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={style.a11yLabel}
      data-testid={`payment-status-chip-${state}`}
      className={cn(
        'inline-flex items-center rounded font-bold uppercase tracking-[0.1em] whitespace-nowrap font-mono align-middle',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn('inline-block rounded-full shrink-0', sz.dot, style.dot)}
      />
      <span aria-hidden="true" className="leading-none">
        {style.glyph}
      </span>
      <span>{text}</span>
    </span>
  );
}
